//#region IMPORTS
import * as XLSX from "xlsx";
import { InstoreOrder } from "../models/instoreOrder";
import { ORDERSTATUS } from "../models/order";
//#endregion

// Track & trace import for instore orders (customer request, Sept 2026):
// an Excel / CSV with the order code (e.g. WEEK__6022__202609141917) and the
// full track & trace link, one row per parcel. Pure helpers — parsing, the
// template and the check step; the wizard is InstoreOrdersTrackAndTraceImport.
//
// An instore order stores its parcels as ONE string, links joined by ";".
// The import only ever ADDS links; existing ones always stay.

export interface ParsedTrackAndTraceRow {
  rowNumber: number; // as in Excel (1-based, header = 1)
  orderCode: string;
  link: string;
}

export type TrackAndTraceRowStatus =
  | "new"
  | "present"
  | "duplicate"
  | "notFound"
  | "skippedStatus"
  | "invalidLink"
  | "missingCode"
  | "lookupFailed";

export interface CheckedTrackAndTraceRow extends ParsedTrackAndTraceRow {
  status: TrackAndTraceRowStatus;
  order?: InstoreOrder;
}

export const TRACK_AND_TRACE_STATUS_LABEL: Record<
  TrackAndTraceRowStatus,
  string
> = {
  new: "will be added",
  present: "already on the order",
  duplicate: "duplicate in file",
  notFound: "order not found",
  skippedStatus: "order cancelled / archived",
  invalidLink: "not a valid link",
  missingCode: "no order code",
  lookupFailed: "could not be checked",
};

export const TEMPLATE_HEADERS = ["ORDER_CODE", "TRACK_AND_TRACE"];

// The ";"-joined parcels of an order as a clean list.
export function splitTrackAndTrace(value?: string | null): string[] {
  return (value ?? "")
    .split(";")
    .map((link) => link.trim())
    .filter(Boolean);
}

// ONE link per row: a row is one order code + one link (one parcel); several
// parcels = several rows with the same order code. So a valid link has no
// whitespace and no ";" — a cell with two links (or the app's own ";"
// separator) is rejected as "not a valid link" instead of being stored as one
// broken entry.
const isLink = (value: string) => /^https?:\/\/[^\s;]+$/i.test(value);

// Reads the first sheet. Columns are found by their header — something with
// "order" for the code, and "track" / "trace" / "link" / "url" for the link.
// Without a recognisable header, but with an order code in the first cell,
// the first two columns are used and row 1 is data. A hyperlink cell counts
// with its target URL, not its display text.
export async function parseTrackAndTraceFile(
  file: File,
): Promise<ParsedTrackAndTraceRow[]> {
  const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet || !sheet["!ref"]) throw new Error("The file is empty.");
  const range = XLSX.utils.decode_range(sheet["!ref"]);

  const cellText = (r: number, c: number): string => {
    const cell = sheet[XLSX.utils.encode_cell({ r, c })];
    if (!cell) return "";
    const value = cell.l?.Target ?? cell.w ?? cell.v;
    return value === undefined || value === null ? "" : String(value).trim();
  };

  const headers: string[] = [];
  for (let c = range.s.c; c <= range.e.c; c++)
    headers.push(cellText(range.s.r, c).toLowerCase());
  const linkIndex = headers.findIndex((h) => /track|trace|link|url/.test(h));
  const orderIndex = headers.findIndex(
    (h, i) => i !== linkIndex && /order/.test(h),
  );

  let orderCol: number;
  let linkCol: number;
  let firstDataRow: number;
  if (orderIndex >= 0 && linkIndex >= 0) {
    orderCol = range.s.c + orderIndex;
    linkCol = range.s.c + linkIndex;
    firstDataRow = range.s.r + 1;
  } else if (cellText(range.s.r, range.s.c).includes("__")) {
    orderCol = range.s.c;
    linkCol = range.s.c + 1;
    firstDataRow = range.s.r;
  } else {
    throw new Error(
      "Could not find an order code column and a track & trace column. Use the template.",
    );
  }

  const rows: ParsedTrackAndTraceRow[] = [];
  for (let r = firstDataRow; r <= range.e.r; r++) {
    const orderCode = cellText(r, orderCol);
    const link = cellText(r, linkCol);
    if (!orderCode && !link) continue;
    rows.push({ rowNumber: r + 1, orderCode, link });
  }
  return rows;
}

export function downloadTrackAndTraceTemplate(): void {
  const worksheet = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS]);
  worksheet["!cols"] = [{ wch: 30 }, { wch: 60 }];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "track and trace");
  XLSX.writeFile(workbook, "track_and_trace_import_template.xlsx");
}

// The check step. `orders` maps each order code to its order, null when it
// does not exist, or undefined when the lookup failed. Rows are checked in
// file order, so the first of two identical rows is the one that counts.
export function checkTrackAndTraceRows(
  rows: ParsedTrackAndTraceRow[],
  orders: Map<string, InstoreOrder | null | undefined>,
): CheckedTrackAndTraceRow[] {
  const seen = new Set<string>();
  return rows.map((row) => {
    if (!row.orderCode) return { ...row, status: "missingCode" };
    if (!isLink(row.link)) return { ...row, status: "invalidLink" };
    const order = orders.get(row.orderCode);
    if (order === undefined) return { ...row, status: "lookupFailed" };
    if (order === null) return { ...row, status: "notFound" };
    if (
      order.order_status_name === ORDERSTATUS.cancelled ||
      order.order_status_name === ORDERSTATUS.archived
    )
      return { ...row, status: "skippedStatus", order };
    if (splitTrackAndTrace(order.track_and_trace).includes(row.link))
      return { ...row, status: "present", order };
    const key = `${row.orderCode}\u0000${row.link}`;
    if (seen.has(key)) return { ...row, status: "duplicate", order };
    seen.add(key);
    return { ...row, status: "new", order };
  });
}
